import { PROJECT_NAME_MAX } from '@mogzo/shared';
import { Button, Dialog, Field, Input, Notice, Panel } from '@mogzo/ui';
import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import { useProject } from '../components/ProjectLayout';
import { ApiError, apiSend, type Project } from '../lib/api';
import { formatDate } from '../lib/format';

function failure(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Could not reach the API. Try again.';
}

function Details({ project, onSaved }: { project: Project; onSaved: (p: Project) => void }) {
  const [draft, setDraft] = useState(project.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!draft.trim()) return setError('Enter the game’s name.');
    setBusy(true);
    setError(null);
    try {
      const updated = await apiSend<Project>('PATCH', `/v1/dev/projects/${project.id}`, {
        name: draft.trim(),
      });
      setSaved(true);
      onSaved(updated);
    } catch (failed) {
      setError(failure(failed));
    } finally {
      setBusy(false);
    }
  }

  const rows = [
    { label: 'Address', value: project.slug, mono: true },
    { label: 'Test community', value: `mogzo.com/test/${project.slug}`, mono: true },
    { label: 'Game page, once published', value: `mogzo.com/games/${project.slug}`, mono: true },
    { label: 'Created', value: formatDate(project.createdAt), mono: false },
  ];

  return (
    <Panel
      title="Project details"
      description="The address is fixed when the project is made, so links to it keep working."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {saved && <Notice tone="success">Saved. The project is now called {project.name}.</Notice>}
        {error && <Notice tone="danger">{error}</Notice>}
        <form
          onSubmit={save}
          style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12 }}
        >
          <Field label="Game name" htmlFor="rename" className="flex-[1_1_320px]">
            <Input
              id="rename"
              maxLength={PROJECT_NAME_MAX}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                setSaved(false);
              }}
            />
          </Field>
          <Button
            type="submit"
            variant="primary"
            loading={busy}
            disabled={draft.trim() === project.name}
          >
            Save name
          </Button>
        </form>
        <dl style={{ margin: 0 }}>
          {rows.map((row) => (
            <div
              key={row.label}
              className="flex flex-wrap justify-between gap-x-4 gap-y-1 border-t border-divider py-3"
            >
              <dt className="text-muted">{row.label}</dt>
              <dd className={row.mono ? 'mz-mono' : ''} style={{ margin: 0 }}>
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </Panel>
  );
}

function ArchivePanel({
  project,
  onChanged,
}: {
  project: Project;
  onChanged: (p: Project) => void;
}) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const archived = Boolean(project.archivedAt);

  async function change(action: 'archive' | 'restore') {
    setBusy(true);
    setError(null);
    try {
      const updated = await apiSend<Project>('POST', `/v1/dev/projects/${project.id}/${action}`);
      if (action === 'archive') navigate('/');
      else onChanged(updated);
    } catch (failed) {
      setError(failure(failed));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title={archived ? 'Archived' : 'Archive this project'}
      description={
        archived
          ? `Archived on ${formatDate(project.archivedAt ?? '')}. Restore it to see it in your project list again.`
          : 'Hides the project from your list and stops new uploads. Nothing is deleted, and you can restore it from Archived projects.'
      }
      actions={
        archived ? (
          <Button onClick={() => change('restore')} loading={busy}>
            Restore project
          </Button>
        ) : (
          <Button variant="danger" onClick={() => setConfirming(true)}>
            Archive project
          </Button>
        )
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Dialog
        open={confirming}
        title={`Archive ${project.name}?`}
        onClose={() => setConfirming(false)}
        footer={
          <>
            <Button onClick={() => setConfirming(false)} disabled={busy}>
              Keep it
            </Button>
            <Button variant="danger" onClick={() => change('archive')} loading={busy}>
              Archive project
            </Button>
          </>
        }
      >
        It leaves your project list and takes no new uploads. Nothing is deleted.
      </Dialog>
    </Panel>
  );
}

/** The Settings tab (M25): the project's name and addresses, and archiving it. */
export function ProjectSettings() {
  const { project, setProject } = useProject();
  return (
    <>
      <Details key={project.id} project={project} onSaved={setProject} />
      <ArchivePanel project={project} onChanged={setProject} />
    </>
  );
}
